import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Save, Loader2, Upload, X, Plus } from "lucide-react";
import { supabase, getCurrentUser } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

// Define the form schema for supplier profile
const supplierProfileSchema = z.object({
  // Basic information
  businessName: z.string().min(2, { message: "Business name must be at least 2 characters" }),
  description: z.string().optional(),
  website: z.string().url({ message: "Please enter a valid URL" }).optional().or(z.literal('')),
  address: z.string().optional(),
  logoUrl: z.string().optional(),
  
  // Business details
  establishmentYear: z.coerce.number().min(1900).max(new Date().getFullYear()).optional(),
  natureOfBusiness: z.string().optional(),
  legalStatus: z.string().optional(),
  
  // Registration details
  gstNumber: z.string().optional(),
  gstRegistrationDate: z.string().optional(),
  panNumber: z.string().optional(),
  importExportCode: z.string().optional(),
  
  // Business metrics
  annualTurnover: z.string().optional(),
  employeeCount: z.string().optional(),
  
  // Categories
  productCategories: z.array(z.string()).default([]),
  
  // Certifications
  certifications: z.array(
    z.object({
      name: z.string(),
      verified: z.boolean().default(false)
    })
  ).default([]),
  
  // Company information
  about: z.string().optional(),
  mission: z.string().optional(),
  vision: z.string().optional(),
  mainMarkets: z.array(z.string()).default([]),
  
  // Verification
  trustSealVerified: z.boolean().optional(),
  machinesearchCertification: z.boolean().optional(),
});

type SupplierProfileFormValues = z.infer<typeof supplierProfileSchema>;

interface Certification {
  name: string;
  verified: boolean;
}

interface SupplierProfileFormProps {
  supplierId: string;
  onSuccess?: () => void;
}

// Define business type options
const businessTypes = [
  "Manufacturer",
  "Wholesaler",
  "Retailer",
  "Service Provider",
  "Exporter",
  "Importer"
];

// Define legal status options
const legalStatusOptions = [
  "Sole Proprietorship",
  "Partnership",
  "Limited Company",
  "Corporation",
  "LLC",
  "LLP"
];

// Define turnover ranges
const turnoverRanges = [
  "Under 5 Lakhs",
  "5 - 25 Lakhs",
  "25 Lakhs - 1 Cr",
  "1 - 5 Cr",
  "5 - 25 Cr",
  "25 - 100 Cr",
  "100 Cr & Above"
];

// Define employee count ranges
const employeeCountRanges = [
  "1-10 People",
  "11-25 People",
  "26-50 People",
  "51-100 People",
  "101-500 People",
  "501-1000 People",
  "1000+ People"
];

export function SupplierProfileForm({ supplierId, onSuccess }: SupplierProfileFormProps) {
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newMarket, setNewMarket] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const { toast } = useToast();

  // Initialize form
  const form = useForm<SupplierProfileFormValues>({
    resolver: zodResolver(supplierProfileSchema),
    defaultValues: {
      businessName: '',
      description: '',
      website: '',
      address: '',
      logoUrl: '',
      establishmentYear: undefined,
      natureOfBusiness: '',
      legalStatus: '',
      gstNumber: '',
      gstRegistrationDate: '',
      panNumber: '',
      importExportCode: '',
      annualTurnover: '',
      employeeCount: '',
      productCategories: [],
      certifications: [],
      about: '',
      mission: '',
      vision: '',
      mainMarkets: [],
      trustSealVerified: false,
      machinesearchCertification: false,
    },
  });

  // Load supplier profile data
  const loadProfile = useCallback(async () => {
    if (!supplierId) return;

    try {
      setLoading(true);
      
      // Get supplier profile data
      const { data: profile, error: profileError } = await supabase
        .from('accounts')
        .select(`
          *,
          supplier_profile:supplier_profiles(*)
        `)
        .eq('id', supplierId)
        .single();

      if (profileError) throw profileError;

      const supplierProfile = profile.supplier_profile;

      // Populate form with existing data
      form.reset({
        businessName: supplierProfile?.business_name || profile.company_name || '',
        description: supplierProfile?.description || '',
        website: supplierProfile?.website || '',
        address: supplierProfile?.address || '',
        logoUrl: supplierProfile?.logo_url || profile.avatar_url || '',
        establishmentYear: supplierProfile?.establishment_year,
        natureOfBusiness: supplierProfile?.nature_of_business || '',
        legalStatus: supplierProfile?.legal_status || '',
        gstNumber: supplierProfile?.gst_number || '',
        gstRegistrationDate: supplierProfile?.gst_registration_date || '',
        panNumber: supplierProfile?.pan_number || '',
        importExportCode: supplierProfile?.import_export_code || '',
        annualTurnover: supplierProfile?.annual_turnover || '',
        employeeCount: supplierProfile?.employee_count || '',
        productCategories: supplierProfile?.product_categories || [],
        certifications: supplierProfile?.certifications || [],
        about: supplierProfile?.about || '',
        mission: supplierProfile?.mission || '',
        vision: supplierProfile?.vision || '',
        mainMarkets: supplierProfile?.main_markets || [],
        trustSealVerified: supplierProfile?.trust_seal_verified || false,
        machinesearchCertification: supplierProfile?.machinesearch_certification || false,
      });

    } catch (error) {
      console.error('Error loading supplier profile:', error);
      toast({
        title: "Error",
        description: "Failed to load profile data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [supplierId, form, toast]);

  // Save profile
  const onSubmit = useCallback(async (data: SupplierProfileFormValues) => {
    try {
      setIsSaving(true);

      // Upsert supplier profile
      const { error } = await supabase
        .from('supplier_profiles')
        .upsert({
          id: supplierId,
          business_name: data.businessName,
          description: data.description,
          website: data.website,
          address: data.address,
          logo_url: data.logoUrl,
          establishment_year: data.establishmentYear,
          nature_of_business: data.natureOfBusiness,
          legal_status: data.legalStatus,
          gst_number: data.gstNumber,
          gst_registration_date: data.gstRegistrationDate,
          pan_number: data.panNumber,
          import_export_code: data.importExportCode,
          annual_turnover: data.annualTurnover,
          employee_count: data.employeeCount,
          product_categories: data.productCategories,
          certifications: data.certifications,
          about: data.about,
          mission: data.mission,
          vision: data.vision,
          main_markets: data.mainMarkets,
          trust_seal_verified: data.trustSealVerified,
          machinesearch_certification: data.machinesearchCertification,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Profile updated successfully!",
      });

      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  }, [supplierId, toast, onSuccess]);

  // Handle logo upload
  const handleLogoUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // Upload file to storage
      const fileName = `${supplierId}/logo-${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('supplier-logos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('supplier-logos')
        .getPublicUrl(fileName);

      // Update form
      form.setValue('logoUrl', urlData.publicUrl);

      toast({
        title: "Success",
        description: "Logo uploaded successfully!",
      });

    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: "Error",
        description: "Failed to upload logo. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  }, [supplierId, form, toast]);

  // Add category
  const addCategory = useCallback(() => {
    if (!newCategory.trim()) return;
    
    const currentCategories = form.getValues('productCategories');
    if (!currentCategories.includes(newCategory.trim())) {
      form.setValue('productCategories', [...currentCategories, newCategory.trim()]);
    }
    setNewCategory('');
  }, [newCategory, form]);

  // Remove category
  const removeCategory = useCallback((category: string) => {
    const currentCategories = form.getValues('productCategories');
    form.setValue('productCategories', currentCategories.filter(c => c !== category));
  }, [form]);

  // Add market
  const addMarket = useCallback(() => {
    if (!newMarket.trim()) return;
    
    const currentMarkets = form.getValues('mainMarkets');
    if (!currentMarkets.includes(newMarket.trim())) {
      form.setValue('mainMarkets', [...currentMarkets, newMarket.trim()]);
    }
    setNewMarket('');
  }, [newMarket, form]);

  // Remove market
  const removeMarket = useCallback((market: string) => {
    const currentMarkets = form.getValues('mainMarkets');
    form.setValue('mainMarkets', currentMarkets.filter(m => m !== market));
  }, [form]);

  // Add certification
  const addCertification = useCallback(() => {
    if (!newCertification.trim()) return;
    
    const currentCertifications = form.getValues('certifications');
    const exists = currentCertifications.some(c => c.name === newCertification.trim());
    
    if (!exists) {
      form.setValue('certifications', [
        ...currentCertifications,
        { name: newCertification.trim(), verified: false }
      ]);
    }
    setNewCertification('');
  }, [newCertification, form]);

  // Remove certification
  const removeCertification = useCallback((index: number) => {
    const currentCertifications = form.getValues('certifications');
    form.setValue('certifications', currentCertifications.filter((_, i) => i !== index));
  }, [form]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Loading profile...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>
              Update your company's basic details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Logo Upload */}
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage src={form.watch('logoUrl')} />
                <AvatarFallback>
                  {form.watch('businessName')?.charAt(0)?.toUpperCase() || 'C'}
                </AvatarFallback>
              </Avatar>
              <div>
                <Label htmlFor="logo-upload">Company Logo</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isUploading}
                    onClick={() => document.getElementById('logo-upload')?.click()}
                  >
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Upload Logo
                  </Button>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="businessName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="https://www.example.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Address</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Business Details */}
        <Card>
          <CardHeader>
            <CardTitle>Business Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="establishmentYear"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Establishment Year</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        {...field}
                        onChange={e => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="natureOfBusiness"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nature of Business</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {businessTypes.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="legalStatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Legal Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {legalStatusOptions.map(status => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="annualTurnover"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Annual Turnover</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {turnoverRanges.map(range => (
                          <SelectItem key={range} value={range}>{range}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="employeeCount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Employee Count</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {employeeCountRanges.map(range => (
                          <SelectItem key={range} value={range}>{range}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Registration Details */}
        <Card>
          <CardHeader>
            <CardTitle>Registration Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="gstNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>GST Number</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter GST number" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="panNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>PAN Number</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter PAN number" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="gstRegistrationDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>GST Registration Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="importExportCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Import Export Code</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter IEC" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Product Categories */}
        <Card>
          <CardHeader>
            <CardTitle>Product Categories</CardTitle>
            <CardDescription>
              Add the main product categories you deal with
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Add product category"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
              />
              <Button type="button" onClick={addCategory} variant="outline">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {form.watch('productCategories').map((category, index) => (
                <Badge key={index} variant="secondary" className="gap-1">
                  {category}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1"
                    onClick={() => removeCategory(category)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Main Markets */}
        <Card>
          <CardHeader>
            <CardTitle>Main Markets</CardTitle>
            <CardDescription>
              Specify your primary geographical markets
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newMarket}
                onChange={(e) => setNewMarket(e.target.value)}
                placeholder="Add market (e.g., North India, Export)"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addMarket())}
              />
              <Button type="button" onClick={addMarket} variant="outline">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {form.watch('mainMarkets').map((market, index) => (
                <Badge key={index} variant="secondary" className="gap-1">
                  {market}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1"
                    onClick={() => removeMarket(market)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Certifications */}
        <Card>
          <CardHeader>
            <CardTitle>Certifications</CardTitle>
            <CardDescription>
              List your business certifications and qualifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newCertification}
                onChange={(e) => setNewCertification(e.target.value)}
                placeholder="Add certification"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
              />
              <Button type="button" onClick={addCertification} variant="outline">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-2">
              {form.watch('certifications').map((cert, index) => (
                <div key={index} className="flex items-center justify-between p-2 border rounded">
                  <span>{cert.name}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant={cert.verified ? "default" : "secondary"}>
                      {cert.verified ? "Verified" : "Pending"}
                    </Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCertification(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Company Information */}
        <Card>
          <CardHeader>
            <CardTitle>Company Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="about"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>About Us</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="mission"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mission</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={2} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="vision"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vision</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={2} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Verification Status */}
        <Card>
          <CardHeader>
            <CardTitle>Verification Status</CardTitle>
            <CardDescription>
              These settings are managed by our verification team
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="trustSealVerified"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Trust Seal Verified</FormLabel>
                    <FormDescription>
                      Your business has been verified with our trust seal
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="machinesearchCertification"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>MachineSearch Certification</FormLabel>
                    <FormDescription>
                      Certified supplier in the MachineSearch network
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={isSaving} size="lg">
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Profile
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
} 